/**
 * Collection template ESM (Phase 10) — thin orchestrator.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 * Updated: 2026-07-14 - Wired sort_by select into the change delegate
 * Updated: 2026-09-26 - Sort is a Filter-style toggle + panel of sort_by radios
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import {
  handleCollectionFiltersChange,
  handleCollectionFilterToggleClick,
  handleDenimFilterCheckboxClick,
  handleRemoveSelectFilter,
  handleSortChange,
  teardownFilters,
} from './collection-filters-controller.js';
import {
  handleShopBySizeChange,
  clearSize,
} from './collection-size-sort.js';
import {
  createOverlayToggleHandler,
  teardownOverlays,
  VIEW_SETTINGS_NAMESPACE,
  FILTERS_NAMESPACE,
  SORT_NAMESPACE,
} from './collection-overlay.js';
import { COLLECTION_SELECTOR } from './dom.js';
import { centerActiveJumpNavs } from './jump-nav.js';

const COLLECTION_DISPLAY_SELECTOR = 'input[name="collection_display"]';
const FITS_OR_FLATS_SELECTOR = 'input[name="image_type"]';
const SHOP_BY_SIZE_SELECTOR = 'select[name="footwear_size"]';
const SORT_OPTION_SELECTOR = '.js-sort-option';
const RESET_SIZE_SELECTOR = '.js-reset-collection-size';
const VIEW_SETTINGS_TOGGLE_SELECTOR = '.js-view-settings-toggle';
const VIEW_SETTINGS_OVERLAY_SELECTOR = '.js-view-settings-overlay';
const FILTERS_OVERLAY_TOGGLE_SELECTOR = '.js-filters-overlay-toggle';
const FILTERS_OVERLAY_SELECTOR = '.js-filters-overlay';
const SORT_OVERLAY_TOGGLE_SELECTOR = '.js-sort-overlay-toggle';
const SORT_OVERLAY_SELECTOR = '.js-sort-overlay';
const FILTERS_SECTION_SELECTOR = '.js-collection-filters';
const REMOVE_SELECT_FILTER_SELECTOR = '.js-remove-select-filter';
const FILTER_INPUT_SELECTOR =
  FILTERS_SECTION_SELECTOR + ' .js-filter input[type="radio"], ' +
  FILTERS_SECTION_SELECTOR + ' .js-filter input[type="checkbox"], ' +
  FILTERS_SECTION_SELECTOR + ' .js-filter select';
const DENIM_CHECKBOX_SELECTOR =
  '.denim-filters .js-collection-filter input[type="checkbox"], ' +
  '.filters-overlay .color-filters .js-collection-filter input[type="checkbox"], ' +
  '.filters-overlay .fabric-filters .js-collection-filter input[type="checkbox"]';

const handleViewSettingsToggle = createOverlayToggleHandler({
  overlaySelector: VIEW_SETTINGS_OVERLAY_SELECTOR,
  namespace: VIEW_SETTINGS_NAMESPACE,
});
const handleFiltersOverlayToggle = createOverlayToggleHandler({
  overlaySelector: FILTERS_OVERLAY_SELECTOR,
  namespace: FILTERS_NAMESPACE,
});
const handleSortOverlayToggle = createOverlayToggleHandler({
  overlaySelector: SORT_OVERLAY_SELECTOR,
  namespace: SORT_NAMESPACE,
});

function applyGrid(value) {
  const collection = document.querySelector(COLLECTION_SELECTOR);
  if (collection) collection.setAttribute('data-grid', value);
}

function syncGridFromAppState() {
  const value = appGet('dom.collection.grid', null);
  if (value == null) return;
  document
    .querySelectorAll(COLLECTION_DISPLAY_SELECTOR)
    .forEach((radio) => {
      radio.checked = radio.value === String(value);
    });
  applyGrid(value);
}

function setCollectionGrid(input) {
  // Apply the DOM change directly. Persisting through app.get/set is for
  // cross-page state (syncGridFromAppState restores it on the next visit) —
  // not for cross-handler dispatch, so there's no need to roundtrip through
  // app.observer. Doing so pre-hydration would also throw, since
  // app.observer's body references $document which is undefined until
  // app.init() runs on DOMContentLoaded (deferred entry scripts execute
  // first).
  applyGrid(input.value);
  safeAppCall('set', 'dom.collection.grid', input.value);
}

function toggleFitsOrFlats(input) {
  const filtersSection = input.closest(FILTERS_SECTION_SELECTOR);
  if (!filtersSection) return;
  const valueLabel = filtersSection.querySelector('.js-view-settings-value');
  const optionLabel = document.querySelector(`label[for="${input.id}"]`);
  const text = optionLabel ? optionLabel.textContent : '';
  if (valueLabel) valueLabel.textContent = text;
  const container = filtersSection.dataset.container;
  if (container) {
    const target = document.querySelector(container);
    if (target) target.setAttribute('data-image-type', input.value);
  }
}

function onPageChange() {
  // ep:change fires on full Turbo Drive nav (not on frame swaps), so the user
  // navigated to a new page. Clear every pending timer + handler the
  // controllers installed on the discarded DOM. (Re-centering the jump nav on
  // navigation is owned by application.esm.js's url.path observer, which fires
  // for every page — including non-collection destinations — so it isn't
  // duplicated here.)
  log('[Collection] ep:change → teardown');
  teardownFilters();
  teardownOverlays();
  syncGridFromAppState();
}

function onPageReady() {
  syncGridFromAppState();
  // Center the jump nav on initial page load — the one moment the global
  // url.path observer doesn't cover (url.path isn't set until the first Turbo
  // navigation).
  centerActiveJumpNavs();
}

// ─── Document delegates ─────────────────────────────────────────────────────

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target.matches(COLLECTION_DISPLAY_SELECTOR)) return setCollectionGrid(target);
  if (target.matches(FITS_OR_FLATS_SELECTOR)) return toggleFitsOrFlats(target);
  if (target.matches(SHOP_BY_SIZE_SELECTOR)) return handleShopBySizeChange(target);
  if (target.matches(SORT_OPTION_SELECTOR)) return handleSortChange(target);
  if (target.matches(FILTER_INPUT_SELECTOR)) return handleCollectionFiltersChange(target);
});

// Coarse short-circuit: every click target this delegate cares about
// lives inside #collection or one of the two fixed-position overlays.
// Skip the per-selector closest() walks otherwise.
const CLICK_ROOT_SELECTOR = '#collection, .filters-overlay, .view-settings-overlay';

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest(CLICK_ROOT_SELECTOR)) return;

  // FILTER_INPUT_SELECTOR and DENIM_CHECKBOX_SELECTOR can both match the
  // same input (a denim filter rendered in the filters section); both
  // handlers must run, so these branches don't return.
  const filterInput = target.closest(FILTER_INPUT_SELECTOR);
  if (filterInput) handleCollectionFilterToggleClick(filterInput);

  const denimCheckbox = target.closest(DENIM_CHECKBOX_SELECTOR);
  if (denimCheckbox) handleDenimFilterCheckboxClick(denimCheckbox);

  const removeSelectFilter = target.closest(REMOVE_SELECT_FILTER_SELECTOR);
  if (removeSelectFilter) return handleRemoveSelectFilter(removeSelectFilter);

  const resetSize = target.closest(RESET_SIZE_SELECTOR);
  if (resetSize) return clearSize(resetSize);

  const viewSettings = target.closest(VIEW_SETTINGS_TOGGLE_SELECTOR);
  if (viewSettings) return handleViewSettingsToggle(viewSettings);

  const filtersOverlay = target.closest(FILTERS_OVERLAY_TOGGLE_SELECTOR);
  if (filtersOverlay) return handleFiltersOverlayToggle(filtersOverlay);

  const sortOverlay = target.closest(SORT_OVERLAY_TOGGLE_SELECTOR);
  if (sortOverlay) return handleSortOverlayToggle(sortOverlay);
});

document.addEventListener('ep:change', onPageChange);
document.addEventListener('ep:ready', onPageReady);

// Initial sync — defer is set on the loader, so the DOM is parsed by now.
syncGridFromAppState();
centerActiveJumpNavs();

log('[Collection] template registered');
